import {
  DRAFT_LINE_WIDTH,
  ROUTE_LINE_SHADOW_BLUR,
  ROUTE_LINE_SHADOW_TRANSLATE,
  ROUTE_LINE_SHADOW_WIDTH,
} from "./line-paint.ts";

/** Map container min side (px) at or above which route strokes stay at editor/full-map width. */
export const ROUTE_LINE_REFERENCE_MIN_PX = 480;

/** Smallest rendered stroke width after downscaling (px). */
export const ROUTE_LINE_WIDTH_FLOOR_PX = 1;

/** Full-size dash patterns for dashed / dotted route strokes (scaled down on small maps). */
export const DASHED_PATTERN: readonly [number, number] = [3, 2];
export const DOTTED_PATTERN: readonly [number, number] = [0.4, 1.6];

export function routeLineScaleFactor(widthPx: number, heightPx: number): number {
  const minDim = Math.min(widthPx, heightPx);
  if (!Number.isFinite(minDim) || minDim <= 0) return 1;
  if (minDim >= ROUTE_LINE_REFERENCE_MIN_PX) return 1;
  return minDim / ROUTE_LINE_REFERENCE_MIN_PX;
}

function scaleNumber(value: number, factor: number): number {
  if (factor >= 0.999) return value;
  return value * factor;
}

type LineWidthExpr = readonly unknown[];

function scaleWidthExpr(factor: number, base: LineWidthExpr): LineWidthExpr {
  if (factor >= 0.999) return base;
  return ["max", ROUTE_LINE_WIDTH_FLOOR_PX, ["*", factor, base]];
}

function scaleDash(pattern: readonly [number, number], factor: number): [number, number] {
  if (factor >= 0.999) return [pattern[0], pattern[1]];
  return [scaleNumber(pattern[0], factor), scaleNumber(pattern[1], factor)];
}

export interface ScaledRouteLinePaint {
  lineWidth: LineWidthExpr;
  shadowWidth: LineWidthExpr;
  shadowBlur: number;
  shadowTranslate: [number, number];
  dashedDasharray: [number, number];
  dottedDasharray: [number, number];
}

/** Paint values for styled route layers at `factor` (1 = full-size map / editor). */
export function scaledRouteLinePaint(factor: number): ScaledRouteLinePaint {
  const shadowWidth = scaleWidthExpr(factor, ROUTE_LINE_SHADOW_WIDTH as LineWidthExpr);
  const lineWidth = scaleWidthExpr(factor, DRAFT_LINE_WIDTH as LineWidthExpr);
  const blur = scaleNumber(ROUTE_LINE_SHADOW_BLUR, factor);
  const translateY = scaleNumber(ROUTE_LINE_SHADOW_TRANSLATE[1], factor);
  return {
    lineWidth,
    shadowWidth,
    shadowBlur: blur,
    shadowTranslate: [0, translateY],
    dashedDasharray: scaleDash(DASHED_PATTERN, factor),
    dottedDasharray: scaleDash(DOTTED_PATTERN, factor),
  };
}
